import { useCallback, useEffect, useRef } from 'react';
import { useChat } from './useChat';
import { useFormContext } from '../contexts/FormContext';
import { useChatState } from './useChatState';
import { useFormSubmission } from './useFormSubmission';
import { useAnalyticsTracking } from './useAnalyticsTracking';
import { useOpenAIResponses } from './useOpenAIResponses';
import { useChatActions } from './useChatActions';
import { useChatCleanup } from './useChatCleanup';
import { useChatActivityTracking } from './useChatActivityTracking';
import { useShiftDigitalTracking } from './useShiftDigitalTracking';
import { usePromptMenu } from './usePromptMenu';
import { waitForShiftPageview } from '@/utils/shiftDigitalTracking';

export const useChatInterface = () => {
  // Chat state management
  const {
    input,
    setInput,
    isExpanded,
    setIsExpanded,
    showNewConversationDialog,
    setShowNewConversationDialog,
    showSMSScreenshotModal,
    setShowSMSScreenshotModal,
    screenshotUrl,
    handleNewConversationClick,
    handleCopyConversation
  } = useChatState();

  // Listen for collapse chat events from button clicks
  useEffect(() => {
    const handleCollapseEvent = () => {
      setIsExpanded(false);
    };

    window.addEventListener('d2c-collapse-chat', handleCollapseEvent);
    
    return () => {
      window.removeEventListener('d2c-collapse-chat', handleCollapseEvent);
    };
  }, [setIsExpanded]);
  
  // OpenAI Responses API management
  const { clearConversation, abortCurrentRequest, incompleteUserMessage } = useOpenAIResponses();
  
  // Form context
  const {
    activeForm,
    isOfferHighlighted,
    formData,
    updateFormData,
    closeForm,
  } = useFormContext();

  // Analytics tracking
  const { trackChatStarted } = useAnalyticsTracking();

  // Activity tracking and inactivity timeout
  const { recordActivity } = useChatActivityTracking({
    isExpanded,
    setIsExpanded,
    closeForm,
    setShowNewConversationDialog
  });

  // Chat functionality
  const { 
    messages, 
    isRestoring,
    isLoading, 
    isSearching,
    sendMessage, 
    clearMessages,
    chatId,
    sessionId,
    dealerId,
    shiftTracking: chatShiftTracking,
    retryLastRequest,
    waitingVideoData,
    streamingIntro,
  } = useChat(setIsExpanded, incompleteUserMessage);
  
  // Shift Digital tracking with sessionId
  const shiftTracking = useShiftDigitalTracking(sessionId);
  
  // Prompt menu with inventory filter and showroom filter support
  const { getCurrentInventoryFilter, getCurrentL1Title, getShowroomFilter } = usePromptMenu();
  
  // DEBUG: Log when hook initializes
  useEffect(() => {
    // Silent
  }, [sessionId, shiftTracking.isShiftDigitalEnabled]);

  // Form submission handling
  const { handleFormSubmit } = useFormSubmission(sendMessage, shiftTracking.trackLead);

  // Track impression after Shift Digital PAGEVIEW fires
  // Shift Digital requires web-based events (pageview) to fire before chat events
  // Use ref to ensure we only set up the wait once per session
  const impressionWaitSetupRef = useRef<string | null>(null);

  useEffect(() => {
    if (!sessionId) return;
    if (!shiftTracking.isShiftDigitalEnabled) return;

    // Only set up the wait once per session
    if (impressionWaitSetupRef.current === sessionId) return;
    impressionWaitSetupRef.current = sessionId;

    waitForShiftPageview(() => {
      shiftTracking.trackImpression();
    });
  }, [sessionId, shiftTracking.isShiftDigitalEnabled, shiftTracking.trackImpression]);

  // Chat actions (message sending)
  const { handleSubmit, handleSendMessage } = useChatActions({
    input,
    setInput,
    isLoading,
    sendMessage,
    recordActivity,
    trackMessage: shiftTracking.trackMessage,
    getCurrentInventoryFilter,
    getCurrentL1Title,
    getShowroomFilter
  });

  // Chat cleanup (new conversation)
  const { handleConfirmNewConversation } = useChatCleanup({
    dealerId,
    clearMessages,
    closeForm,
    setShowNewConversationDialog,
    setInput,
    abortCurrentRequest,
    clearConversation
  });

  return {
    // State
    input,
    setInput,
    isExpanded,
    setIsExpanded,
    showNewConversationDialog,
    setShowNewConversationDialog,
    showSMSScreenshotModal,
    setShowSMSScreenshotModal,
    screenshotUrl,
    
    // Chat state
    messages,
    isRestoring,
    isLoading,
    isSearching,
    chatId,
    sessionId,
    dealerId,
    waitingVideoData,
    streamingIntro,
    
    // Form state
    activeForm,
    isOfferHighlighted,
    formData,
    updateFormData,
    closeForm,
    
    // Handlers
    handleFormSubmit,
    handleNewConversationClick,
    handleConfirmNewConversation,
    handleCopyConversation: (messagesContainerRef?: React.RefObject<HTMLElement>) => handleCopyConversation(messages, messagesContainerRef),
    handleSendMessage,
    
    // Shift Digital tracking
    shiftTracking: chatShiftTracking,
    
    // Retry mechanism
    retryLastRequest,
  };
};
